import type { DubbingRequest, DubbingResponse, ApiResponse, LanguageVoiceConfig } from '../types';

import { handleError } from '../utils/errorHandler';
import { JSON_HEADERS } from '../utils/httpUtils';
import { isE2ETestMode } from '../utils/apiMode';

/**
 * Audio Dubbing Request
 */
export interface AudioDubbingRequest {
  audio_url: string;
  source_language?: string | null;
  target_languages: string[];
  voice_configs?: LanguageVoiceConfig[];
  timing_mode?: 'synchronized' | 'free';
  export_format?: 'zip' | 'none';
}

/**
 * Audio Dubbing Response
 */
export interface AudioDubbingResponse {
  success: boolean;
  job_id: string;
  download_url: string | null;
  detected_language: string;
  audio_tracks: Record<string, number>;
  languages_processed: string[];
  speed_factors?: Record<string, number> | null;
  processing_time_seconds: number;
  error?: string | null;
}

/**
 * Dubbing API Service
 * Handles video and audio dubbing operations with multilingual support
 */
export class DubbingAPI {
  /**
   * Get the base URL for the API
   */
  private static async getBaseUrl(): Promise<string> {
    // Reuse existing API base URL logic from VideoGenerationAPI
    const { VideoGenerationAPI } = await import('./api');
    return VideoGenerationAPI.getBaseUrl();
  }

  /**
   * Get authorization token for authenticated requests.
   * Returns null if Firebase is not available or user is not authenticated.
   */
  private static async getAuthToken(): Promise<string | null> {
    try {
      const { firebaseClient } = await import('./firebase');
      const token = await firebaseClient.getIdToken();
      if (token) {
        console.log('[DubbingAPI] Got Firebase token successfully');
      }
      return token;
    } catch (err) {
      console.warn('[DubbingAPI] Firebase error getting token:', err);
      return null;
    }
  }

  /**
   * Submit a video for dubbing
   * @param request - Dubbing configuration request
   * @returns Dubbing response with task ID
   */
  static async dubVideo(request: DubbingRequest): Promise<DubbingResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const authToken = await this.getAuthToken();

      const headers: Record<string, string> = { ...JSON_HEADERS };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('[DubbingAPI] Added Authorization header to video dubbing request');
      } else {
        console.warn('[DubbingAPI] No auth token available for video dubbing request');
      }

      const response = await fetch(`${baseUrl}/api/v1/dubbing`, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      // Handle standardized API response format:
      // { success: bool, data: { job_id, status, message, ... }, error: null, metadata: {...} }
      const rawResponse = (await response.json()) as {
        success: boolean;
        data?: {
          job_id?: string;
          status?: string;
          message?: string;
          download_url?: string | null;
          audio_tracks?: Record<string, number> | null;
          processing_time_seconds?: number | null;
          segments_processed?: number | null;
        };
        error?: { code: string; message: string } | null;
      };

      if (!response.ok || !rawResponse.success) {
        throw new Error(rawResponse.error?.message ?? 'Dubbing request failed');
      }

      const data = rawResponse.data;
      return {
        success: true,
        task_id: data?.job_id ?? '',
        media_kind: 'video',
        download_url: data?.download_url ?? null,
        audio_tracks: data?.audio_tracks ?? null,
        processing_time_seconds: data?.processing_time_seconds ?? null,
        segments_processed: data?.segments_processed ?? null,
        error: null,
        message: data?.message ?? 'Dubbing started successfully',
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Dubbing', 'Failed to start dubbing');
      return {
        success: false,
        task_id: '',
        media_kind: 'video',
        download_url: null,
        audio_tracks: null,
        processing_time_seconds: null,
        segments_processed: null,
        error: errorMessage,
      };
    }
  }

  /**
   * Get dubbing task status
   * Uses the generic /api/v1/jobs/{job_id} endpoint which handles all async jobs
   * @param taskId - Task ID (job_id) returned from dubVideo
   * @returns Task status response
   */
  static async getDubbingStatus(taskId: string): Promise<{
    success: boolean;
    stage?: string;
    progress?: number;
    message?: string;
    // Server-synced timing fields for ElapsedTimeDisplay
    started_at?: string | null;
    completed_at?: string | null;
    elapsed_seconds?: number | null;
    estimated_remaining_seconds?: number | null;
    data?: DubbingResponse;
    error?: string;
  }> {
    try {
      const baseUrl = await this.getBaseUrl();
      const authToken = await this.getAuthToken();

      const headers: Record<string, string> = {};
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }

      // Use the generic jobs endpoint instead of dubbing-specific endpoint
      const response = await fetch(`${baseUrl}/api/v1/jobs/${taskId}`, { headers });
      const rawData = await response.json();

      if (!response.ok) {
        throw new Error(rawData.error?.message || rawData.error || 'Failed to get dubbing status');
      }

      // Backend returns standardized response:
      // { success, data: { job_id, status, progress, message, result_url, extra_metadata: { stage, ... } }, error }
      const jobData = rawData.data;

      // Debug: log raw response
      console.log('[DubbingAPI] Raw status response:', {
        rawData,
        jobData,
        extraMetadata: jobData?.extra_metadata,
        progress: jobData?.progress,
        status: jobData?.status,
      });

      // Get stage directly from extra_metadata.stage (set by DubbingTaskStoreAdapter)
      // If not available, use the job status as stage
      let stage: string;
      if (jobData?.extra_metadata?.stage) {
        // Use explicit stage from backend (transcription, translation, tts, etc.)
        stage = jobData.extra_metadata.stage as string;
      } else if (jobData?.status === 'completed') {
        stage = 'completed';
      } else if (jobData?.status === 'failed' || jobData?.status === 'error') {
        stage = 'error';
      } else {
        // For pending or processing without explicit stage
        stage = jobData?.status || 'pending';
      }

      console.log('[DubbingAPI] Extracted values:', { stage, progress: jobData?.progress });

      // result_url is the single canonical field set by the BE on all completed
      // jobs; extra_metadata.download_url carries the same value for stores that
      // also expose it. ONE url for both dubbing kinds — there is no separate
      // "video url" any more, and inventing one is what broke the results panel.
      const downloadUrl = jobData?.result_url || jobData?.extra_metadata?.download_url || null;
      // Explicit kind from the backend. Absent on cloud jobs whose store does
      // not track it, in which case the caller falls back to the media type the
      // user actually uploaded rather than guessing from URL shapes.
      const mediaKind = jobData?.extra_metadata?.media_kind ?? null;
      // Local filesystem path, desktop only. Present in extra_metadata all
      // along; the FE simply never read it, so "Show in Folder" had nothing to
      // open and every desktop result offered an HTTP download instead.
      const outputFilePath = jobData?.extra_metadata?.output_file_path ?? null;

      // Single explicit success contract: status is 'completed' AND result_url is present.
      // Implicit/legacy proof-of-success branches are removed (RULE #-2).
      const isCompleted = jobData?.status === 'completed' && Boolean(jobData?.result_url);

      return {
        success: rawData.success,
        stage,
        progress: jobData?.progress ?? 0, // progress is at data level, not in extra_metadata
        message: jobData?.message || '',
        // Server-synced timing fields for ElapsedTimeDisplay
        started_at: jobData?.started_at || null,
        completed_at: jobData?.completed_at || null,
        elapsed_seconds: jobData?.elapsed_seconds || null,
        estimated_remaining_seconds: jobData?.estimated_remaining || null,
        data: isCompleted
          ? {
              success: true,
              task_id: taskId,
              media_kind: mediaKind,
              download_url: downloadUrl,
              output_file_path: outputFilePath,
              detected_language: jobData?.extra_metadata?.detected_language || null,
              languages_processed: jobData?.extra_metadata?.languages_processed || [],
              speed_factors: jobData?.extra_metadata?.speed_factors || null,
              audio_tracks: jobData?.extra_metadata?.audio_tracks || null,
              segments_processed: jobData?.extra_metadata?.segments_processed || null,
              processing_time_seconds: null,
              error: null,
            }
          : undefined,
        error: jobData?.error || (jobData?.status === 'failed' ? jobData?.message : undefined),
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Dubbing', 'Failed to get dubbing status');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Upload video file and get URL for dubbing
   * @param file - Video file to upload
   * @param onProgress - Optional progress callback (0-100)
   * @returns URL to the uploaded video
   */
  static async uploadVideo(file: File, onProgress?: (progress: number) => void): Promise<string> {
    return this.uploadWithProgress(
      file,
      onProgress,
      '/api/v1/dubbing/upload',
      'video',
      'video_url',
      'Upload'
    );
  }

  /**
   * Shared XHR upload helper for video/audio dubbing uploads.
   *
   * Consolidates the previously-duplicated uploadVideo/uploadAudio bodies (Stage 12
   * dedup): identical auth-token fetch order, FormData build, progress events,
   * 200-success JSON parse, abort/error handling, and getBaseUrl resolution. The
   * two reject messages that had drifted via copy-paste are unified here to the
   * more informative VIDEO form (status includes statusText; network error includes
   * readyState).
   *
   * @param file - Media file to upload
   * @param onProgress - Optional progress callback (0-100)
   * @param endpoint - API path, e.g. '/api/v1/dubbing/upload'
   * @param fieldName - FormData field name for the file
   * @param urlKey - Response data key holding the uploaded URL
   * @param logLabel - Console log prefix, e.g. 'Upload' / 'Audio upload'
   * @returns URL to the uploaded media
   */
  private static async uploadWithProgress(
    file: File,
    onProgress: ((progress: number) => void) | undefined,
    endpoint: string,
    fieldName: 'video' | 'audio',
    urlKey: 'video_url' | 'audio_url',
    logLabel: string
  ): Promise<string> {
    // Get auth token first (outside the Promise)
    const authToken = await this.getAuthToken();

    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append(fieldName, file);

      const xhr = new XMLHttpRequest();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable && onProgress) {
          const percentComplete = (e.loaded / e.total) * 100;
          onProgress(percentComplete);
        }
      });

      xhr.addEventListener('load', async () => {
        console.log(`[DubbingAPI] ${logLabel} response status:`, xhr.status);
        console.log(`[DubbingAPI] ${logLabel} response text:`, xhr.responseText?.substring(0, 500));
        if (xhr.status === 200) {
          try {
            // Handle standardized API response format:
            // { success: bool, data: { <urlKey>, upload_id }, error: null, metadata: {...} }
            const response = JSON.parse(xhr.responseText) as {
              success: boolean;
              data?: { video_url?: string; audio_url?: string; upload_id?: string };
              error?: { code: string; message: string } | null;
            };
            const uploadedUrl = response.data?.[urlKey];
            if (response.success && uploadedUrl) {
              console.log(`[DubbingAPI] ${logLabel} successful, URL:`, uploadedUrl);
              resolve(uploadedUrl);
            } else {
              console.error(`[DubbingAPI] ${logLabel} response not successful:`, response);
              reject(new Error(response.error?.message || 'Upload failed'));
            }
          } catch (parseError) {
            console.error(
              `[DubbingAPI] Failed to parse ${logLabel.toLowerCase()} response:`,
              parseError
            );
            reject(new Error('Failed to parse upload response'));
          }
        } else {
          console.error(`[DubbingAPI] ${logLabel} failed with status:`, xhr.status, xhr.statusText);
          // Unified to the more informative VIDEO form (includes statusText).
          reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
        }
      });

      xhr.addEventListener('error', (e) => {
        console.error(`[DubbingAPI] ${logLabel} XHR error event:`, e);
        console.error('[DubbingAPI] XHR readyState:', xhr.readyState, 'status:', xhr.status);
        // Unified to the more informative VIDEO form (includes readyState).
        reject(new Error(`Upload failed: Network error (readyState: ${xhr.readyState})`));
      });

      xhr.addEventListener('abort', () => {
        console.warn(`[DubbingAPI] ${logLabel} aborted`);
        reject(new Error('Upload cancelled'));
      });

      this.getBaseUrl()
        .then((baseUrl) => {
          const uploadUrl = `${baseUrl}${endpoint}`;
          console.log(`[DubbingAPI] Starting ${logLabel.toLowerCase()} to:`, uploadUrl);
          console.log('[DubbingAPI] File:', file.name, 'Size:', file.size, 'Type:', file.type);
          xhr.open('POST', uploadUrl);
          // CSRF protection header required by serverless API middleware
          xhr.setRequestHeader('X-Requested-With', 'XMLHttpRequest');
          // Add authorization header if token is available
          if (authToken) {
            xhr.setRequestHeader('Authorization', `Bearer ${authToken}`);
            console.log('[DubbingAPI] Added Authorization header');
          } else {
            console.warn('[DubbingAPI] No auth token available for upload');
          }
          // E2E bypass header — independent of authToken. Real users never
          // see this branch (isE2ETestMode is gated on localStorage flag).
          if (isE2ETestMode()) {
            xhr.setRequestHeader('X-E2E-Test', 'true');
          }
          xhr.send(formData);
        })
        .catch((err) => {
          console.error('[DubbingAPI] Failed to get base URL:', err);
          reject(err);
        });
    });
  }

  /**
   * Cancel a dubbing task
   * @param taskId - Task ID to cancel
   * @returns Success status
   */
  static async cancelDubbing(taskId: string): Promise<ApiResponse<Record<string, unknown>>> {
    try {
      const baseUrl = await this.getBaseUrl();
      const authToken = await this.getAuthToken();

      const headers: Record<string, string> = {
        'X-Requested-With': 'XMLHttpRequest',
      };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
      }
      if (isE2ETestMode()) {
        headers['X-E2E-Test'] = 'true';
      }

      const response = await fetch(`${baseUrl}/api/v1/dubbing/${taskId}/cancel`, {
        method: 'POST',
        headers,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to cancel dubbing');
      }

      return {
        success: true,
        data,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Dubbing', 'Failed to cancel dubbing');
      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  // ============================================================================
  // AUDIO DUBBING METHODS
  // ============================================================================

  /**
   * Upload audio file and get URL for dubbing
   * @param file - Audio file to upload
   * @param onProgress - Optional progress callback (0-100)
   * @returns URL to the uploaded audio
   */
  static async uploadAudio(file: File, onProgress?: (progress: number) => void): Promise<string> {
    return this.uploadWithProgress(
      file,
      onProgress,
      '/api/v1/dubbing/audio-dubbing/upload',
      'audio',
      'audio_url',
      'Audio upload'
    );
  }

  /**
   * Submit an audio file for dubbing
   * @param request - Audio dubbing configuration request
   * @returns Audio dubbing response with task ID and download URL
   */
  static async dubAudio(request: AudioDubbingRequest): Promise<AudioDubbingResponse> {
    try {
      const baseUrl = await this.getBaseUrl();
      const authToken = await this.getAuthToken();
      const dubUrl = `${baseUrl}/api/v1/dubbing/audio-dubbing`;
      console.log('[DubbingAPI] Starting audio dubbing request to:', dubUrl);
      console.log('[DubbingAPI] Request payload:', JSON.stringify(request, null, 2));

      const headers: Record<string, string> = { ...JSON_HEADERS };
      if (authToken) {
        headers['Authorization'] = `Bearer ${authToken}`;
        console.log('[DubbingAPI] Added Authorization header to audio dubbing request');
      } else {
        console.warn('[DubbingAPI] No auth token available for audio dubbing request');
      }

      const response = await fetch(dubUrl, {
        method: 'POST',
        headers,
        body: JSON.stringify(request),
      });

      console.log('[DubbingAPI] Audio dubbing response status:', response.status);

      // Handle standardized API response format
      const rawResponse = (await response.json()) as {
        success: boolean;
        data?: {
          job_id: string;
          download_url: string | null;
          detected_language: string;
          audio_tracks: Record<string, number>;
          languages_processed: string[];
          speed_factors?: Record<string, number> | null;
          processing_time_seconds: number;
        };
        error?: { code: string; message: string } | null;
      };

      console.log('[DubbingAPI] Audio dubbing response:', JSON.stringify(rawResponse, null, 2));

      if (!response.ok || !rawResponse.success) {
        console.error('[DubbingAPI] Audio dubbing failed:', rawResponse.error);
        throw new Error(rawResponse.error?.message || 'Audio dubbing request failed');
      }

      const data = rawResponse.data;
      console.log('[DubbingAPI] Audio dubbing successful, download_url:', data?.download_url);
      return {
        success: true,
        job_id: data?.job_id || '',
        download_url: data?.download_url || null,
        detected_language: data?.detected_language || 'unknown',
        audio_tracks: data?.audio_tracks || {},
        languages_processed: data?.languages_processed || [],
        speed_factors: data?.speed_factors || null,
        processing_time_seconds: data?.processing_time_seconds || 0,
        error: null,
      };
    } catch (error: unknown) {
      const errorMessage = handleError(error, 'Dubbing', 'Failed to start audio dubbing');
      console.error('[DubbingAPI] Audio dubbing error:', errorMessage);
      return {
        success: false,
        job_id: '',
        download_url: null,
        detected_language: '',
        audio_tracks: {},
        languages_processed: [],
        speed_factors: null,
        processing_time_seconds: 0,
        error: errorMessage,
      };
    }
  }

  /**
   * Upload media (video or audio) based on file type
   * @param file - Media file to upload
   * @param onProgress - Optional progress callback (0-100)
   * @returns URL to the uploaded media and the detected media type
   */
  static async uploadMedia(
    file: File,
    onProgress?: (progress: number) => void
  ): Promise<{ url: string; mediaType: 'video' | 'audio' }> {
    // Detect media type from file
    const isAudio =
      file.type.startsWith('audio/') ||
      ['mp3', 'wav', 'aac', 'flac', 'm4a', 'ogg', 'opus'].includes(
        file.name.split('.').pop()?.toLowerCase() || ''
      );

    if (isAudio) {
      const url = await this.uploadAudio(file, onProgress);
      return { url, mediaType: 'audio' };
    } else {
      const url = await this.uploadVideo(file, onProgress);
      return { url, mediaType: 'video' };
    }
  }
}
